import { defineStore, storeToRefs } from 'pinia'
import { ref, computed, watch } from 'vue'
import router from '@/router'
import { type Step, type SignupEntryResolution } from './signupFlow'
import { type SurveyStorage, type UserAssessmentSurveyStorage } from '@gh/common/models'
import { useCustomerStore } from './customer'
import { useCoachConsultationsStore } from './coachConsultations'
import { SignupPersistence } from './signupPersistence'
import { useAuthStore } from './auth'
import { debounce } from 'throttle-debounce'
import { leadQuestions } from '@/components/signup/leadQuestions'
import { useUTMStore } from './utm'
import createLogger from '@/utils/logger'
import { SignupPages } from '@/router/signupRoutes'
import { CustomerPages } from '@/router/customerRoutes'
import type {
  ClassId,
  ClinicalInsuranceCategory,
  ClinicalConsultationInterest,
  SignupPlanSelection,
} from '@gh/common/models'
import { SIGNUP_PLAN_SELECTION_FREE_TRIAL, PaymentMethodEnum } from '@gh/common/models'
import {
  isSignupClinicalConsultationOffered,
  resolveSignupClinicalConsultationEligibility,
  type SignupClinicalConsultationEligibility,
} from '@gh/common/models'
import { useBrandingCodeStore } from './brandingCode'
import { useBrandingStore } from './branding'
import { usePaymentMethodStore } from './paymentMethod'
import { useExperienceFlowStore } from './experienceFlow'
import { useProductsStore } from './products'
import { useStripeCouponsStore } from './stripeCoupons'
import { waitUntilReady } from '@/utils/storeUtils'
import {
  isConsultationBookingSignupPath,
  resolveCanonicalConsultationBookingSignupPathFromProductState,
} from '@/components/consultation/consultationBookingSignupRouting'
import { useClinicalConsultationBookingStore } from '@/stores/clinicalConsultationBooking'
import { isInPostPaymentConfirmationSession } from '@/stores/clinicalConsultationBookingFlow'

const logger = createLogger('stores/signup.ts')

const SIGNUP_RESUME_WELCOME_ACK_PREFIX = 'signup-resume-welcome-ack'
const SIGNUP_FLOW_ACTIVE_PREFIX = 'signup-flow-active'

const SignupStepToPath: { [key in NonNullable<Step>]: string } = {
  'get-started': SignupPages.GetStarted.path,
  'lead-question': SignupPages.LeadQuestion.path,
  'lead-motivation': SignupPages.LeadMotivation.path,
  'referral-selection': SignupPages.ReferralSelection.path,
  'thank-you-for-creating-account': SignupPages.ThankYouForSubscribing.path,
  'create-account': SignupPages.CreateAccount.path,
  'user-assessment': SignupPages.UserAssessment.path,
  'sponsor-screen': SignupPages.SponsorScreen.path,
  'almost-there': SignupPages.AlmostThere.path,
  'class-selection': SignupPages.ClassSelection.path,
  'patient-information': SignupPages.PatientInformation.path,
  'insurance-category': SignupPages.InsuranceCategory.path,
  'clinical-consultation-interest': SignupPages.ClinicalConsultationInterest.path,
  'signup-completed-message': SignupPages.SignupCompletedMessage.path,
  'plan-selection': SignupPages.PlanSelection.path,
  'stripe-checkout': SignupPages.StripeCheckout.path,
  'thank-you-for-subscribing': SignupPages.ThankYouForSubscribing.path,
  'schedule-consultation': SignupPages.ScheduleConsultation.path,
  complete: CustomerPages.Home.path,
}

export const useSignupStore = defineStore('signup', () => {
  const generateSessionId = () => {
    return Math.random().toString(36).substring(2, 15)
  }

  const authStore = useAuthStore()
  const customerStore = useCustomerStore()
  const brandingCodeStore = useBrandingCodeStore()
  const brandingStore = useBrandingStore()
  const productStore = useProductsStore()
  const stripeCouponsStore = useStripeCouponsStore()
  const selectedProduct = computed(() => {
    if (
      selectedPlan.value === 'not-selected' ||
      selectedPlan.value === SIGNUP_PLAN_SELECTION_FREE_TRIAL
    ) {
      return undefined
    }
    return productStore.products.find((p) => p.id === selectedPlan.value)
  })

  const consultationIncludedFromProduct = computed(() => {
    return selectedProduct.value?.consultation ?? false
  })

  const consultationIncludedCombined = computed(() => {
    return (
      consultationIncludedFromPaymentMethod.value ||
      consultationIncludedFromProduct.value ||
      !!customerStore.customer?.consultationIncluded
    )
  })

  const accountCreated = computed(() => {
    return !!customerStore.customer
  })

  const coachConsultationsStore = useCoachConsultationsStore()
  const consultationScheduled = computed(() => {
    return coachConsultationsStore.consultations.length > 0
  })

  const signupCompleted = computed(() => {
    return customerStore.signupCompleted
  })

  const paymentMethodStore = usePaymentMethodStore()
  const experienceFlowStore = useExperienceFlowStore()

  const paymentCompleted = computed(() => paymentMethodStore.paymentCompleted)
  const paymentNeeded = computed(() => paymentMethodStore.paymentNeeded)
  const showPlanSelectionAndStripeCheckout = computed(
    () => paymentMethodStore.showPlanSelectionAndStripeCheckout,
  )
  const trialDaysForSignup = computed(() => brandingStore.trialDays)
  const requiresPaidSignupSteps = computed(
    () => showPlanSelectionAndStripeCheckout.value && trialDaysForSignup.value <= 0,
  )

  /** Plan-selection step: choose free trial vs paid product when storefront shows checkout UX. */
  const signupHasPlanSelectionStep = computed(() => showPlanSelectionAndStripeCheckout.value)

  /** After plan-selection, user continues through Stripe (paid product or legacy mandatory pay). */
  const signupPaidCheckoutAfterPlan = computed(() => {
    if (!showPlanSelectionAndStripeCheckout.value) return false
    if (selectedPlan.value === SIGNUP_PLAN_SELECTION_FREE_TRIAL) return false
    if (selectedPlan.value === 'not-selected') {
      return requiresPaidSignupSteps.value
    }
    return true
  })

  /** "Account created" step: no paid checkout, or user chose free trial (trial starts on home). */
  const includeSignupCompletedMessageInFlow = ref(true)

  const consultationIncludedFromPaymentMethod = storeToRefs(paymentMethodStore).consultationIncluded
  const isDefaultPaymentMethodSignup = computed(
    () => paymentMethodStore.PaymentMethod === PaymentMethodEnum.Default,
  )
  const sponsorScreenAfterClassSelection =
    storeToRefs(experienceFlowStore).sponsorScreenAfterClassSelection

  const persistanceId = computed(() => {
    return customerStore.customer?.id ?? null
  })

  const isLoadedFromFirestore = ref<boolean>(false)
  const authLoading = computed(() => authStore.isLoading)
  const customerLoading = computed(() => customerStore.isLoading)
  // const isLoadedFromFirestoreLoaded = computed(() => !!isLoadedFromFirestore.value);

  const patientInformationFilledIn = computed(() => {
    return !!customerStore.customer?.firstName
  })

  const isLoading = computed(() => {
    return authLoading.value || customerLoading.value || !isLoadedFromFirestore.value
  })

  const currentStep = ref<Step>(null)
  /** While Google create-account checks for an existing customer after popup sign-in. */
  const googleSignupDuplicateCheckInFlight = ref(false)
  const resumeWelcomeDismissed = ref(false)
  const sessionId = ref<string | null>(generateSessionId())
  const leadQuestionId = ref<string | null>(null)
  const selectedPlan = ref<SignupPlanSelection>('not-selected')
  const selectedClasses = ref<ClassId[]>([])
  const clinicalInsuranceCategory = ref<ClinicalInsuranceCategory | null>(null)
  const clinicalConsultationInterest = ref<ClinicalConsultationInterest | null>(null)
  const patientInformationSurvey = ref<SurveyStorage>({
    data: {
      firstName: null,
      lastName: null,
      mobilePhone: null,
      stateOfResidence: null,
      dateOfBirth: null,
      hearAboutUs: null,
      textMessages: null,
    },
    currentPageNo: 0,
  })

  const userAssessmentSurvey = ref<UserAssessmentSurveyStorage>({
    data: {
      gender: null,
      cancerType_women: null,
      cancerType_nonWomen: null,
      cancerType_general: null,
      cancerType_combined: null,
      supportNeeds: null,
      dailyActivityAbility: null,
      currentSymptoms: null,
      pastHolisticPractices: null,
    },
    currentPageNo: 0,
  })

  function getClinicalConsultationEligibility(): SignupClinicalConsultationEligibility {
    const bookingStore = useClinicalConsultationBookingStore()
    void bookingStore.subscribeLicensedStates()
    return resolveSignupClinicalConsultationEligibility({
      stateOfResidence:
        customerStore.customer?.stateOfResidence ??
        patientInformationSurvey.value.data.stateOfResidence,
      insuranceCategory: clinicalInsuranceCategory.value,
      supportedStateCodes: bookingStore.supportedStateCodes,
    })
  }

  watch(
    [
      currentStep,
      leadQuestionId,
      selectedPlan,
      selectedClasses,
      patientInformationSurvey,
      userAssessmentSurvey,
      patientInformationFilledIn,
      clinicalInsuranceCategory,
      clinicalConsultationInterest,
    ],
    () => saveToFirestoreDebounced(),
    { deep: true },
  )

  const loadFromFirestore = async () => {
    if (isLoadedFromFirestore.value) {
      return
    }
    try {
      const data = await SignupPersistence.load(persistanceId.value)
      if (data) {
        sessionId.value = data.sessionId
        leadQuestionId.value = data.leadQuestionId
        selectedPlan.value = data.selectedPlan
        selectedClasses.value = data.selectedClasses
        patientInformationSurvey.value = data.patientInformationSurvey
        userAssessmentSurvey.value = data.userAssessmentSurvey
        currentStep.value = data.currentStep ?? null
        clinicalInsuranceCategory.value = data.clinicalInsuranceCategory ?? null
        clinicalConsultationInterest.value = data.clinicalConsultationInterest ?? null
        logger.log('Data loaded from Firestore:', persistanceId.value)
      }
      isLoadedFromFirestore.value = true
    } catch (error) {
      console.error('Failed to load from Firestore:', error)
      isLoadedFromFirestore.value = true
    }
  }

  const saveToFirestore = async () => {
    try {
      const stepToPersist =
        !signupCompleted.value && currentStep.value && currentStep.value !== 'complete'
          ? currentStep.value
          : undefined

      await SignupPersistence.save(persistanceId.value, {
        sessionId: sessionId.value,
        leadQuestionId: leadQuestionId.value,
        selectedPlan: selectedPlan.value,
        selectedClasses: selectedClasses.value,
        patientInformationSurvey: patientInformationSurvey.value,
        userAssessmentSurvey: userAssessmentSurvey.value,
        patientInformationFilledIn: patientInformationFilledIn.value,
        clinicalInsuranceCategory: clinicalInsuranceCategory.value,
        clinicalConsultationInterest: clinicalConsultationInterest.value,
        ...(stepToPersist ? { currentStep: stepToPersist } : {}),
      })
    } catch (error) {
      console.error('Save failed:', error)
    }
  }

  watch(
    persistanceId,
    async () => {
      isLoadedFromFirestore.value = false
      await loadFromFirestore()
      useClinicalConsultationBookingStore().subscribeLicensedStates()
      logger.log('loadFromFirestore done')
    },
    { immediate: true },
  )

  const saveToFirestoreDebounced = debounce(1000, saveToFirestore, { atBegin: false })

  function changeCurrentStep(newCurrentStep: NonNullable<Step>) {
    logger.log(`currentStep changed from ${currentStep.value} to ${newCurrentStep}`)
    markSignupFlowActiveThisSession()
    currentStep.value = newCurrentStep
    const route =
      SignupStepToPath[currentStep.value as NonNullable<Step>] ?? SignupPages.GetStarted.path

    logger.log('ensureRoute', route)
    if (window.location.pathname !== route) {
      router.push(route)
    }
  }

  const getStepsInOrder = (currentStep: NonNullable<Step>): NonNullable<Step>[] => {
    const steps: NonNullable<Step>[] = []
    steps.push('get-started')
    steps.push('lead-question')
    steps.push('lead-motivation')

    if (
      currentStep === 'referral-selection' ||
      !(
        brandingCodeStore.getBrandingCode() ||
        stripeCouponsStore.getPromoCode() ||
        brandingCodeStore.isBrandingCodeCaptureSkipped()
      )
    ) {
      steps.push('referral-selection')
    }

    steps.push('create-account')
    steps.push('user-assessment')
    steps.push('class-selection')

    if (currentStep === 'patient-information' || !patientInformationFilledIn.value) {
      steps.push('patient-information')
    }

    if (isDefaultPaymentMethodSignup.value) {
      if (
        sponsorScreenAfterClassSelection.value &&
        (currentStep === 'sponsor-screen' || patientInformationFilledIn.value)
      ) {
        steps.push('sponsor-screen')
      }
      if (currentStep === 'plan-selection' || patientInformationFilledIn.value) {
        steps.push('plan-selection')
      }
    }

    if (!isDefaultPaymentMethodSignup.value) {
      steps.push('sponsor-screen')
      if (
        currentStep === 'almost-there' ||
        signupHasPlanSelectionStep.value ||
        consultationIncludedFromPaymentMethod.value
      ) {
        steps.push('almost-there')
      }

      if (currentStep === 'plan-selection' || signupHasPlanSelectionStep.value) {
        steps.push('plan-selection')
      }

      if (currentStep === 'stripe-checkout' || signupPaidCheckoutAfterPlan.value) {
        steps.push('stripe-checkout')
      }
      if (currentStep === 'stripe-checkout' || signupPaidCheckoutAfterPlan.value) {
        steps.push('thank-you-for-subscribing')
      }

      if (currentStep === 'schedule-consultation' || consultationIncludedCombined.value) {
        steps.push('schedule-consultation')
      }
    }

    steps.push('complete')

    logger.log('getStepsInOrder', {
      steps,
      currentStep,
    })
    return steps
  }

  const getNextStep = (currentStep: NonNullable<Step>): NonNullable<Step> => {
    const steps: NonNullable<Step>[] = getStepsInOrder(currentStep)

    const index = steps.indexOf(currentStep)
    const nextStep = steps[index + 1]

    if (!nextStep) {
      return 'complete'
    }
    return nextStep
  }

  function completeGetStarted() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeLeadQuestion(id: string) {
    leadQuestionId.value = id

    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeLeadMotivation() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeReferralSelection() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  async function completeCreateAccount() {
    useUTMStore().captureUTMParams({ update: true })
    await Promise.all([
      authStore.refreshToken(true),
      customerStore.untilLoadingIsDone(),
      brandingCodeStore.removeBrandingCode(),
      brandingCodeStore.clearSkipBrandingCodeCaptureConfig(),
      paymentMethodStore.untilLoadingIsDone(),
    ])
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeUserAssessment() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeClassSelection(classIds: ClassId[]) {
    selectedClasses.value = classIds

    changeCurrentStep(getNextStep(currentStep.value!))
  }

  async function completePatientInformation() {
    await waitUntilReady({ continueOnTrue: () => patientInformationFilledIn.value })
    if (isDefaultPaymentMethodSignup.value) {
      selectedPlan.value = SIGNUP_PLAN_SELECTION_FREE_TRIAL
      router.push(SignupPages.AlmostThere.path)
      return
    }
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function navigateToPlanSelectionAfterProfileLoader() {
    changeCurrentStep('plan-selection')
  }

  function navigateToSponsorScreenAfterProfileLoader() {
    changeCurrentStep('sponsor-screen')
  }

  function completeInsuranceCategory(category: ClinicalInsuranceCategory) {
    clinicalInsuranceCategory.value = category
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function clearClinicalConsultationInterest() {
    clinicalConsultationInterest.value = null
    saveToFirestoreDebounced()
  }

  function acceptClinicalConsultationInterest() {
    clinicalConsultationInterest.value = 'yes'
    saveToFirestoreDebounced()
  }

  function declineClinicalConsultationInterest() {
    clinicalConsultationInterest.value = 'no'
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function declineClinicalConsultationInterestWithoutStepChange() {
    clinicalConsultationInterest.value = 'no'
    saveToFirestoreDebounced()
  }

  function completeClinicalConsultationBooking() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeSignupCompleted() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeAlmostThere() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function navigateToCommunityFreeTrialLoader() {
    markSignupFlowActiveThisSession()
    void router.push({
      path: SignupPages.FreeTrialLoader.path,
      query: { destination: 'community' },
    })
  }

  function completeSponsorScreen() {
    if (!isDefaultPaymentMethodSignup.value && !consultationIncludedFromPaymentMethod.value) {
      navigateToCommunityFreeTrialLoader()
      return
    }
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completePlanSelection() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeStripeCheckout(_consultation: boolean) {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeThankYouForSubscribing() {
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function completeScheduleConsultation() {
    if (!isDefaultPaymentMethodSignup.value) {
      navigateToCommunityFreeTrialLoader()
      return
    }
    changeCurrentStep(getNextStep(currentStep.value!))
  }

  function init() {
    logger.log('init')
  }

  function cleanup() {
    logger.log('cleanup')
    isLoadedFromFirestore.value = false
    clearSignupFlowActiveThisSession()
    resumeWelcomeDismissed.value = false
    currentStep.value = null
    sessionId.value = generateSessionId()
    leadQuestionId.value = null
    selectedPlan.value = 'not-selected'
    patientInformationSurvey.value = {
      data: {
        firstName: null,
        lastName: null,
        mobilePhone: null,
        stateOfResidence: null,
        dateOfBirth: null,
        hearAboutUs: null,
        textMessages: null,
      },
      currentPageNo: 0,
    }
    userAssessmentSurvey.value = {
      data: {
        gender: null,
        cancerType_women: null,
        cancerType_nonWomen: null,
        cancerType_general: null,
        cancerType_combined: null,
        supportNeeds: null,
        dailyActivityAbility: null,
        currentSymptoms: null,
        pastHolisticPractices: null,
      },
      currentPageNo: 0,
    }
    selectedClasses.value = []
    selectedPlan.value = 'not-selected'
    clinicalInsuranceCategory.value = null
    clinicalConsultationInterest.value = null
  }

  function syncSelectedPlanFromCustomerPayment() {
    const customer = customerStore.customer
    if (selectedPlan.value !== 'not-selected' || !customer?.signupPaymentInfo?.stripeProductId) {
      return
    }
    const product = productStore.products.find(
      (p) => p.stripeProductId === customer.signupPaymentInfo?.stripeProductId,
    )
    if (product) {
      selectedPlan.value = product.id
    }
  }

  const signupPaymentCompleteForFlow = computed(
    () => !signupPaidCheckoutAfterPlan.value || paymentCompleted.value,
  )

  /** Default signup resume — booking routes deep-link when interest is yes. */
  function inferDefaultSignupResumeStep(): NonNullable<Step> {
    if (
      currentStep.value === 'insurance-category' ||
      currentStep.value === 'clinical-consultation-interest'
    ) {
      return 'plan-selection'
    }
    if (clinicalConsultationInterest.value === 'yes') {
      return 'plan-selection'
    }
    if (patientInformationFilledIn.value) {
      if (sponsorScreenAfterClassSelection.value && currentStep.value !== 'plan-selection') {
        return 'sponsor-screen'
      }
      return 'plan-selection'
    }
    return 'user-assessment'
  }

  function resolveClinicalConsultationBookingResumePath(): string {
    const eligibility = getClinicalConsultationEligibility()
    if (!isSignupClinicalConsultationOffered(eligibility)) {
      return SignupPages.ClinicalConsultationInterest.path
    }

    if (clinicalConsultationInterest.value !== 'yes') {
      return SignupPages.ClinicalConsultationInterest.path
    }

    const bookingStore = useClinicalConsultationBookingStore()
    const {
      currentStep,
      draftSubPhase,
      reservedSubPhase,
      isAvailabilityLoading,
      isConsultationSnapshotLoading,
      isSessionHydrating,
      billingPath,
      productStep,
      productPreflowComplete,
      hasScheduledConsultation,
      customer: bookingCustomer,
      consultationId,
      customerCheckoutAcknowledged,
      customerAwaitingCheckoutPropagation,
    } = storeToRefs(bookingStore)

    const isContinuingBooking =
      Boolean(consultationId.value) || Boolean(customerStore.customer?.activeClinicalConsultationId)

    return resolveCanonicalConsultationBookingSignupPathFromProductState({
      productStep: productStep.value,
      productPreflowComplete: productPreflowComplete.value,
      hasScheduledConsultation: hasScheduledConsultation.value,
      currentStep: currentStep.value,
      draftSubPhase: draftSubPhase.value,
      reservedSubPhase: reservedSubPhase.value,
      billingPath: billingPath.value ?? bookingStore.consultation?.billingPath ?? null,
      isContinuingBooking,
      isResumingConsultation:
        isSessionHydrating.value ||
        (Boolean(consultationId.value) && isConsultationSnapshotLoading.value),
      isAvailabilityLoading: isAvailabilityLoading.value,
      hasActiveBookingSession: Boolean(bookingCustomer.value?.id),
      entrySource: 'signup',
      inPostPaymentConfirmationSession: isInPostPaymentConfirmationSession({
        customerCheckoutAcknowledged: customerCheckoutAcknowledged.value,
        customerAwaitingCheckoutPropagation: customerAwaitingCheckoutPropagation.value,
        consultationId: consultationId.value,
      }),
    })
  }

  function resolveSignupStepPath(resumeStep: NonNullable<Step>): string {
    if (
      isDefaultPaymentMethodSignup.value &&
      (resumeStep === 'insurance-category' || resumeStep === 'clinical-consultation-interest')
    ) {
      return SignupPages.PlanSelection.path
    }
    if (resumeStep === 'clinical-consultation-interest') {
      if (clinicalConsultationInterest.value === 'yes') {
        return resolveClinicalConsultationBookingResumePath()
      }
      if (
        customerStore.customer?.activeClinicalConsultationId &&
        clinicalConsultationInterest.value !== 'no'
      ) {
        return resolveClinicalConsultationBookingResumePath()
      }
    }
    return SignupStepToPath[resumeStep]
  }

  function inferLegacyStartStep(): NonNullable<Step> {
    if (!accountCreated.value) {
      return 'get-started'
    }

    if (isDefaultPaymentMethodSignup.value && patientInformationFilledIn.value) {
      return inferDefaultSignupResumeStep()
    }

    if (
      accountCreated.value &&
      patientInformationFilledIn.value &&
      signupPaymentCompleteForFlow.value &&
      consultationIncludedCombined.value &&
      !consultationScheduled.value
    ) {
      return 'schedule-consultation'
    }

    if (
      accountCreated.value &&
      patientInformationFilledIn.value &&
      signupPaymentCompleteForFlow.value &&
      consultationIncludedCombined.value &&
      consultationScheduled.value
    ) {
      if (isDefaultPaymentMethodSignup.value) {
        return 'signup-completed-message'
      }
      return 'schedule-consultation'
    }

    return 'user-assessment'
  }

  function getSignupResumeStep(): NonNullable<Step> {
    syncSelectedPlanFromCustomerPayment()

    if (currentStep.value && currentStep.value !== 'complete') {
      if (
        isDefaultPaymentMethodSignup.value &&
        (currentStep.value === 'insurance-category' ||
          currentStep.value === 'clinical-consultation-interest')
      ) {
        return 'plan-selection'
      }
      return currentStep.value
    }

    return inferLegacyStartStep()
  }

  function getResumeWelcomeAckStorageKey(): string | null {
    const customerId = customerStore.customer?.id
    if (!customerId) {
      return null
    }
    return `${SIGNUP_RESUME_WELCOME_ACK_PREFIX}-${customerId}`
  }

  function getSignupFlowActiveStorageKey(): string | null {
    const customerId = customerStore.customer?.id
    if (!customerId) {
      return null
    }
    return `${SIGNUP_FLOW_ACTIVE_PREFIX}-${customerId}`
  }

  function markSignupFlowActiveThisSession(): void {
    const key = getSignupFlowActiveStorageKey()
    if (key) {
      sessionStorage.setItem(key, '1')
    }
  }

  function clearSignupFlowActiveThisSession(): void {
    const key = getSignupFlowActiveStorageKey()
    if (key) {
      sessionStorage.removeItem(key)
    }
  }

  function isSignupFlowActiveThisSession(): boolean {
    const key = getSignupFlowActiveStorageKey()
    if (!key) {
      return false
    }
    return sessionStorage.getItem(key) === '1'
  }

  function isSignupResumeWelcomeAcknowledged(): boolean {
    const key = getResumeWelcomeAckStorageKey()
    if (!key) {
      return false
    }
    return sessionStorage.getItem(key) === '1'
  }

  function acknowledgeSignupResumeWelcome(): void {
    const key = getResumeWelcomeAckStorageKey()
    if (key) {
      sessionStorage.setItem(key, '1')
    }
    resumeWelcomeDismissed.value = true
  }

  const shouldShowSignupResumeWelcome = computed(() => {
    if (resumeWelcomeDismissed.value) {
      return false
    }
    const entry = resolveSignupEntry()
    return entry.kind === 'resume' && entry.phase === 'welcome'
  })

  /**
   * Single signup entry graph (after auth + customer load):
   * - complete → home
   * - in-flow → fresh signup this session (no welcome)
   * - resume/welcome → welcome overlay on resume step URL
   * - resume/step → go to persisted or legacy step
   */
  function resolveSignupEntry(): SignupEntryResolution {
    if (!accountCreated.value || !isLoadedFromFirestore.value || paymentMethodStore.isLoading) {
      return { kind: 'loading' }
    }

    syncSelectedPlanFromCustomerPayment()

    if (signupCompleted.value) {
      return { kind: 'complete' }
    }

    const resumeStep = getSignupResumeStep()
    const stepPath = resolveSignupStepPath(resumeStep)

    if (isSignupFlowActiveThisSession()) {
      return { kind: 'in-flow' }
    }

    if (!isSignupResumeWelcomeAcknowledged()) {
      return {
        kind: 'resume',
        phase: 'welcome',
        resumeStep,
        path: stepPath,
      }
    }

    return {
      kind: 'resume',
      phase: 'step',
      resumeStep,
      path: stepPath,
    }
  }

  /** Only redirect target for incomplete signup navigation — guards call this alone. */
  function getSignupEntryRedirectPath(
    currentPath: string,
    { isSignupRoute }: { isSignupRoute: boolean },
  ): string | null {
    if (isConsultationBookingSignupPath(currentPath)) {
      if (clinicalConsultationInterest.value === 'yes') {
        return null
      }
      if (customerStore.customer?.activeClinicalConsultationId) {
        return null
      }
      return SignupPages.ClinicalConsultationInterest.path
    }

    const entry = resolveSignupEntry()
    logger.log('resolveSignupEntry', entry, { currentPath, isSignupRoute })

    switch (entry.kind) {
      case 'loading':
      case 'complete':
        return null
      case 'in-flow':
        if (!isSignupRoute) {
          return resolveSignupStepPath(getSignupResumeStep())
        }
        return null
      case 'resume':
        if (currentPath === entry.path) {
          return null
        }
        return entry.path
    }
  }

  function ensureClinicalBookingRouteAllowed() {
    if (signupCompleted.value) {
      return
    }

    if (clinicalConsultationInterest.value === 'no') {
      router.push(SignupPages.SignupCompletedMessage.path)
      return
    }

    if (clinicalConsultationInterest.value !== 'yes') {
      const hasActiveConsult = Boolean(customerStore.customer?.activeClinicalConsultationId)
      if (!hasActiveConsult) {
        router.push(SignupPages.ClinicalConsultationInterest.path)
        return
      }
    }

    if (currentStep.value !== 'clinical-consultation-interest') {
      currentStep.value = 'clinical-consultation-interest'
      saveToFirestoreDebounced()
    }
  }

  function redirectToSignupStep(requestedStep: Step) {
    if (signupCompleted.value) {
      router.push(CustomerPages.Home.path)
      return
    }
    const redirectPath = getSignupEntryRedirectPath(router.currentRoute.value.path, {
      isSignupRoute: true,
    })
    if (redirectPath) {
      logger.log(`Redirecting via signup entry to ${redirectPath} from ${requestedStep}`)
      router.push(redirectPath)
      return
    }
    const resumeStep = getSignupResumeStep()
    logger.log(`Redirecting to signup step ${resumeStep} from ${requestedStep}`)
    changeCurrentStep(resumeStep)
  }

  function completeSignupResumeWelcome() {
    acknowledgeSignupResumeWelcome()
    markSignupFlowActiveThisSession()
  }

  function allowedStepChange(currentStep: NonNullable<Step>, nextStep: NonNullable<Step>) {
    const StepsInOrder: NonNullable<Step>[] = getStepsInOrder(currentStep)

    const currentStepIndex = StepsInOrder.indexOf(currentStep)
    const nextStepIndex = StepsInOrder.indexOf(nextStep)
    const allowed = Math.abs(currentStepIndex - nextStepIndex) <= 1

    logger.log('allowedStepChange', {
      currentStep,
      nextStep,
      currentStepIndex,
      nextStepIndex,
      allowed,
    })

    return allowed
  }

  function ensureStepAllowed(step: Step) {
    // called on page load
    // currentStep is set to the previous step or null if directly loaded
    logger.log('ensureStepAllowed', step)
    if (!step) {
      logger.log('ensureStepAllowed no step')
      return
    }

    if (signupCompleted.value && step !== 'signup-completed-message') {
      logger.log('Customer has completed signup, redirecting to home')
      router.push(CustomerPages.Home.path)
      // window.location.href = CustomerPages.Home.path
      return
    }

    if (currentStep.value === null) {
      logger.log('ensureStepAllowed no currentStep')
      if (step === 'schedule-consultation' && consultationScheduled.value) {
        return changeCurrentStep('schedule-consultation')
      }
      return redirectToSignupStep(step)
    }

    let shouldRedirect = false

    switch (step) {
      case 'get-started':
        if (accountCreated.value) {
          shouldRedirect = true
        }
        break
      case 'lead-question':
        if (accountCreated.value) {
          shouldRedirect = true
        }
        break
      case 'lead-motivation':
        if (accountCreated.value) {
          shouldRedirect = true
        }
        break
      case 'create-account':
        if (accountCreated.value) {
          shouldRedirect = true
        }
        break
      case 'user-assessment':
        if (!accountCreated.value || (paymentNeeded.value && paymentCompleted.value)) {
          shouldRedirect = true
        }
        break
      case 'sponsor-screen':
        if (
          !accountCreated.value ||
          (paymentNeeded.value && paymentCompleted.value) ||
          (isDefaultPaymentMethodSignup.value &&
            !experienceFlowStore.sponsorScreenAfterClassSelection)
        ) {
          shouldRedirect = true
        }
        break
      case 'class-selection':
        if (!accountCreated.value || (paymentNeeded.value && paymentCompleted.value)) {
          shouldRedirect = true
        }
        break
      case 'patient-information':
        if (!accountCreated.value || (paymentNeeded.value && paymentCompleted.value)) {
          shouldRedirect = true
        } else if (patientInformationFilledIn.value) {
          const stepsInOrder = getStepsInOrder(step)
          const nextStep =
            stepsInOrder[Math.min(stepsInOrder.indexOf(step) + 1, stepsInOrder.length - 1)]
          const previousStep = stepsInOrder[Math.max(stepsInOrder.indexOf(step) - 1, 0)]
          if (currentStep.value === nextStep) {
            // back button clicked
            return changeCurrentStep(previousStep)
          } else {
            return changeCurrentStep(nextStep)
          }
        }
        break
      case 'insurance-category':
        shouldRedirect = true
        break
      case 'clinical-consultation-interest':
        if (!accountCreated.value || !isDefaultPaymentMethodSignup.value) {
          shouldRedirect = true
        } else {
          shouldRedirect = true
        }
        break
      case 'almost-there':
        if (!accountCreated.value || (paymentNeeded.value && paymentCompleted.value)) {
          shouldRedirect = true
        } else if (isDefaultPaymentMethodSignup.value && !patientInformationFilledIn.value) {
          shouldRedirect = true
        }
        break
      case 'plan-selection':
        if (!accountCreated.value || (paymentNeeded.value && paymentCompleted.value)) {
          shouldRedirect = true
        } else if (isDefaultPaymentMethodSignup.value && !patientInformationFilledIn.value) {
          shouldRedirect = true
        } else if (
          !isDefaultPaymentMethodSignup.value &&
          !signupHasPlanSelectionStep.value &&
          !showPlanSelectionAndStripeCheckout.value
        ) {
          shouldRedirect = true
        }
        break
      case 'stripe-checkout':
        if (
          !accountCreated.value ||
          (paymentNeeded.value && paymentCompleted.value) ||
          !signupPaidCheckoutAfterPlan.value
        ) {
          shouldRedirect = true
        }
        break
      case 'thank-you-for-subscribing':
        if (
          !accountCreated.value ||
          !(paymentNeeded.value && paymentCompleted.value) ||
          consultationScheduled.value
        ) {
          shouldRedirect = true
        }
        break
      case 'schedule-consultation':
        if (!accountCreated.value || (paymentNeeded.value && !paymentCompleted.value)) {
          shouldRedirect = true
        }
        if (!consultationIncludedCombined.value) {
          shouldRedirect = true
        }
        break
      case 'signup-completed-message':
        if (
          !accountCreated.value ||
          !includeSignupCompletedMessageInFlow.value ||
          !isDefaultPaymentMethodSignup.value
        ) {
          shouldRedirect = true
        } else if (signupCompleted.value) {
          shouldRedirect = true
        } else if (clinicalConsultationInterest.value === 'yes') {
          shouldRedirect = true
        }
        break
      case 'complete':
        if (signupCompleted.value) {
          router.push(CustomerPages.Home.path)
        }
        break
      default:
    }

    if (shouldRedirect || !allowedStepChange(currentStep.value!, step!)) {
      return redirectToSignupStep(step)
    }
    logger.log('setting currentStep to', step, 'from', currentStep.value)
    // once we've determined that the step is allowed, we can set the currentStep to the new step
    // to stay consistent, setting currentStep in changeCurrentStep instead of setting it here
    return changeCurrentStep(step)
  }

  const leadQuestionAnswer = computed(() => {
    return leadQuestions.find((question) => question.id === leadQuestionId.value)?.text ?? ''
  })

  function signOut() {
    cleanup()
  }

  function setGoogleSignupDuplicateCheckInFlight(inFlight: boolean) {
    googleSignupDuplicateCheckInFlight.value = inFlight
  }

  async function untilLoadingIsDone() {
    const ready = await waitUntilReady({
      continueOnTrue: () =>
        !isLoading.value && !paymentMethodStore.isLoading && !productStore.isLoading,
    })
    if (ready) {
      syncSelectedPlanFromCustomerPayment()
    }
    return ready
  }

  return {
    isLoading,
    currentStep,
    sessionId,
    leadQuestionId,
    leadQuestionAnswer,
    patientInformationSurvey,
    userAssessmentSurvey,
    accountCreated,
    selectedPlan,
    checkoutCompleted: signupCompleted,
    selectedClasses,
    persistanceId,
    patientInformationFilledIn,
    consultationScheduled,
    paymentCompleted,
    getSignupResumeStep,
    resolveSignupEntry,
    getSignupEntryRedirectPath,
    shouldShowSignupResumeWelcome,
    completeSignupResumeWelcome,
    clinicalInsuranceCategory,
    clinicalConsultationInterest,
    isDefaultPaymentMethodSignup,
    getClinicalConsultationEligibility,
    completeInsuranceCategory,
    acceptClinicalConsultationInterest,
    clearClinicalConsultationInterest,
    declineClinicalConsultationInterest,
    declineClinicalConsultationInterestWithoutStepChange,
    navigateToPlanSelectionAfterProfileLoader,
    navigateToSponsorScreenAfterProfileLoader,
    completeClinicalConsultationBooking,
    completeGetStarted,
    completeLeadQuestion,
    completeLeadMotivation,
    completeReferralSelection,
    completePatientInformation,
    completeCreateAccount,
    completeUserAssessment,
    completePlanSelection,
    completeStripeCheckout,
    completeClassSelection,
    completeSponsorScreen,
    completeScheduleConsultation,
    completeThankYouForSubscribing,
    completeAlmostThere,
    completeSignupCompleted,
    ensureStepAllowed,
    ensureClinicalBookingRouteAllowed,
    init,
    cleanup,
    saveToFirestore,
    loadFromFirestore,
    signOut,
    untilLoadingIsDone,
    googleSignupDuplicateCheckInFlight,
    setGoogleSignupDuplicateCheckInFlight,
  }
})
